import {
  devicePlatformPoliciesResponseSchema,
  devicePlatformPolicyMutationResponseSchema,
  devicePlatformPolicyUpdateRequestSchema,
  hqDeviceDetailsResponseSchema,
  hqDeviceCreateRequestSchema,
  hqDeviceOperationsResponseSchema,
  hqDeviceReEnrollmentRequestSchema,
  hqDeviceUpdateRequestSchema,
  hqFleetResponseSchema,
  schoolDeviceEnrollmentResponseSchema,
  stationReleaseCreateRequestSchema,
  stationReleaseMutationResponseSchema,
  stationReleasesResponseSchema,
  stationReleaseTransitionRequestSchema,
  deviceAccessoryCreateRequestSchema,
  deviceAccessoryUpdateRequestSchema,
  deviceConditionCheckCreateRequestSchema,
  deviceServiceOrderCreateRequestSchema,
  deviceServiceOrderUpdateRequestSchema,
  type HqFleetQuery,
  type HqDeviceUpdateRequest,
  type HqDeviceCreateRequest,
  type HqDeviceReEnrollmentRequest,
  type DeviceAccessoryCreateRequest,
  type DeviceAccessoryUpdateRequest,
  type DeviceConditionCheckCreateRequest,
  type DeviceOperationAttachmentTargetType,
  type DevicePlatform,
  type DevicePlatformPolicyUpdateRequest,
  type DeviceServiceOrderCreateRequest,
  type DeviceServiceOrderUpdateRequest,
  type StationReleaseCreateRequest,
  type StationReleaseTransitionRequest,
} from "@kindtutor/api-contract";
import {
  fetchApiBlob,
  localizedApiErrorSchema as apiErrorSchema,
  parseApiInput,
  throwApiError,
} from "@kindtutor/auth-client";
import { apiBaseURL, apiFetch } from "../auth-client";

export async function fetchHqFleet(input: Partial<HqFleetQuery> = {}) {
  const query = new URLSearchParams();
  if (input.q) query.set("q", input.q);
  if (input.platform) query.set("platform", input.platform);
  if (input.status) query.set("status", input.status);
  if (input.lifecycleState) query.set("lifecycleState", input.lifecycleState);
  if (input.releaseRing) query.set("releaseRing", input.releaseRing);
  if (input.schoolId) query.set("schoolId", input.schoolId);
  if (input.managed !== undefined) query.set("managed", String(input.managed));
  if (input.sort) query.set("sort", input.sort);
  if (input.direction) query.set("direction", input.direction);
  if (input.cursor) query.set("cursor", input.cursor);
  if (input.limit) query.set("limit", String(input.limit));
  const suffix = query.size ? `?${query.toString()}` : "";
  const response = await apiFetch(`${apiBaseURL}/v1/hq/devices${suffix}`, {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (!response.ok)
    await throwApiError(response, "Nie udało się wczytać floty urządzeń.");
  return hqFleetResponseSchema.parse(await response.json());
}

export async function createHqDevice(values: HqDeviceCreateRequest) {
  const input = parseApiInput(hqDeviceCreateRequestSchema, values);
  const response = await apiFetch(`${apiBaseURL}/v1/hq/devices`, {
    method: "POST",
    credentials: "include",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  const body: unknown = await response.json();
  if (!response.ok) {
    const error = apiErrorSchema.safeParse(body);
    throw new Error(
      error.success
        ? error.data.error.message
        : "Nie udało się dodać urządzenia do ewidencji.",
    );
  }
  return hqDeviceDetailsResponseSchema.parse(body);
}

export async function fetchStationReleases() {
  const response = await apiFetch(`${apiBaseURL}/v1/hq/station-releases`, {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (!response.ok)
    await throwApiError(response, "Nie udało się wczytać wydań station.");
  return stationReleasesResponseSchema.parse(await response.json());
}

export async function fetchDevicePlatformPolicies() {
  const response = await apiFetch(
    `${apiBaseURL}/v1/hq/device-platform-policies`,
    {
      credentials: "include",
      headers: { Accept: "application/json" },
    },
  );
  if (!response.ok)
    await throwApiError(
      response,
      "Nie udało się wczytać polityk wersji systemu.",
    );
  return devicePlatformPoliciesResponseSchema.parse(await response.json());
}

export async function updateDevicePlatformPolicy(
  platform: DevicePlatform,
  values: DevicePlatformPolicyUpdateRequest,
) {
  const input = parseApiInput(devicePlatformPolicyUpdateRequestSchema, values);
  const response = await apiFetch(
    `${apiBaseURL}/v1/hq/device-platform-policies/${encodeURIComponent(platform)}`,
    {
      method: "PUT",
      credentials: "include",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(input),
    },
  );
  if (!response.ok)
    await throwApiError(response, "Nie udało się zapisać polityki systemu.");
  return devicePlatformPolicyMutationResponseSchema.parse(
    await response.json(),
  );
}

export async function createStationRelease(
  values: StationReleaseCreateRequest,
) {
  const input = parseApiInput(stationReleaseCreateRequestSchema, values);
  const response = await apiFetch(`${apiBaseURL}/v1/hq/station-releases`, {
    method: "POST",
    credentials: "include",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  if (!response.ok)
    await throwApiError(response, "Nie udało się utworzyć wydania station.");
  return stationReleaseMutationResponseSchema.parse(await response.json());
}

export async function transitionStationRelease(
  releaseId: string,
  values: StationReleaseTransitionRequest,
) {
  const input = parseApiInput(stationReleaseTransitionRequestSchema, values);
  const response = await apiFetch(
    `${apiBaseURL}/v1/hq/station-releases/${encodeURIComponent(releaseId)}/transition`,
    {
      method: "PUT",
      credentials: "include",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(input),
    },
  );
  if (!response.ok)
    await throwApiError(response, "Nie udało się zmienić etapu wydania.");
  return stationReleaseMutationResponseSchema.parse(await response.json());
}

export async function fetchHqDevice(deviceId: string) {
  const response = await apiFetch(
    `${apiBaseURL}/v1/hq/devices/${encodeURIComponent(deviceId)}`,
    { credentials: "include", headers: { Accept: "application/json" } },
  );
  if (!response.ok)
    await throwApiError(response, "Nie udało się wczytać urządzenia.");
  return hqDeviceDetailsResponseSchema.parse(await response.json());
}

export async function updateHqDevice(
  deviceId: string,
  values: HqDeviceUpdateRequest,
) {
  const input = parseApiInput(hqDeviceUpdateRequestSchema, values);
  const response = await apiFetch(
    `${apiBaseURL}/v1/hq/devices/${encodeURIComponent(deviceId)}`,
    {
      method: "PUT",
      credentials: "include",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(input),
    },
  );
  const body: unknown = await response.json();
  if (!response.ok) {
    const error = apiErrorSchema.safeParse(body);
    throw new Error(
      error.success
        ? error.data.error.message
        : "Nie udało się zapisać urządzenia.",
    );
  }
  return hqDeviceDetailsResponseSchema.parse(body);
}

export async function reEnrollHqDevice(
  deviceId: string,
  values: HqDeviceReEnrollmentRequest,
) {
  const input = parseApiInput(hqDeviceReEnrollmentRequestSchema, values);
  const response = await apiFetch(
    `${apiBaseURL}/v1/hq/devices/${encodeURIComponent(deviceId)}/re-enrollment`,
    {
      method: "POST",
      credentials: "include",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(input),
    },
  );
  const body: unknown = await response.json();
  if (!response.ok) {
    const error = apiErrorSchema.safeParse(body);
    throw new Error(
      error.success
        ? error.data.error.message
        : "Nie udało się ponownie wdrożyć urządzenia.",
    );
  }
  return schoolDeviceEnrollmentResponseSchema.parse(body);
}

export async function fetchHqDeviceOperations(deviceId: string) {
  const response = await apiFetch(
    `${apiBaseURL}/v1/hq/devices/${encodeURIComponent(deviceId)}/operations`,
    { credentials: "include", headers: { Accept: "application/json" } },
  );
  if (!response.ok)
    await throwApiError(response, "Nie udało się wczytać obsługi sprzętu.");
  return hqDeviceOperationsResponseSchema.parse(await response.json());
}

async function mutateHqDeviceOperations(
  path: string,
  method: "POST" | "PUT",
  input: unknown,
) {
  const response = await apiFetch(`${apiBaseURL}${path}`, {
    method,
    credentials: "include",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify(input),
  });
  if (!response.ok)
    await throwApiError(response, "Nie udało się zapisać obsługi sprzętu.");
  return hqDeviceOperationsResponseSchema.parse(await response.json());
}

export function addHqDeviceAccessory(
  deviceId: string,
  values: DeviceAccessoryCreateRequest,
) {
  const input = parseApiInput(deviceAccessoryCreateRequestSchema, values);
  return mutateHqDeviceOperations(
    `/v1/hq/devices/${encodeURIComponent(deviceId)}/accessories`,
    "POST",
    input,
  );
}

export function updateHqDeviceAccessory(
  deviceId: string,
  accessoryId: string,
  values: DeviceAccessoryUpdateRequest,
) {
  const input = parseApiInput(deviceAccessoryUpdateRequestSchema, values);
  return mutateHqDeviceOperations(
    `/v1/hq/devices/${encodeURIComponent(deviceId)}/accessories/${encodeURIComponent(accessoryId)}`,
    "PUT",
    input,
  );
}

export function addHqDeviceConditionCheck(
  deviceId: string,
  values: DeviceConditionCheckCreateRequest,
) {
  const input = parseApiInput(deviceConditionCheckCreateRequestSchema, values);
  return mutateHqDeviceOperations(
    `/v1/hq/devices/${encodeURIComponent(deviceId)}/condition-checks`,
    "POST",
    input,
  );
}

export function createHqDeviceServiceOrder(
  deviceId: string,
  values: DeviceServiceOrderCreateRequest,
) {
  const input = parseApiInput(deviceServiceOrderCreateRequestSchema, values);
  return mutateHqDeviceOperations(
    `/v1/hq/devices/${encodeURIComponent(deviceId)}/service-orders`,
    "POST",
    input,
  );
}

export function updateHqDeviceServiceOrder(
  deviceId: string,
  orderId: string,
  values: DeviceServiceOrderUpdateRequest,
) {
  const input = parseApiInput(deviceServiceOrderUpdateRequestSchema, values);
  return mutateHqDeviceOperations(
    `/v1/hq/devices/${encodeURIComponent(deviceId)}/service-orders/${encodeURIComponent(orderId)}`,
    "PUT",
    input,
  );
}

export async function uploadHqDeviceOperationAttachment(input: {
  deviceId: string;
  targetType: DeviceOperationAttachmentTargetType;
  targetId: string;
  file: File;
}) {
  const query = new URLSearchParams({
    targetType: input.targetType,
    targetId: input.targetId,
    filename: input.file.name,
  });
  const response = await apiFetch(
    `${apiBaseURL}/v1/hq/devices/${encodeURIComponent(input.deviceId)}/operation-attachments?${query}`,
    {
      method: "POST",
      credentials: "include",
      headers: { Accept: "application/json", "Content-Type": input.file.type },
      body: input.file,
    },
  );
  if (!response.ok)
    await throwApiError(response, "Nie udało się dodać załącznika.");
  return hqDeviceOperationsResponseSchema.parse(await response.json());
}

export async function downloadHqDeviceOperationAttachment(
  deviceId: string,
  attachmentId: string,
) {
  return fetchApiBlob(
    apiFetch,
    `${apiBaseURL}/v1/hq/devices/${encodeURIComponent(deviceId)}/operation-attachments/${encodeURIComponent(attachmentId)}/content`,
    { credentials: "include", headers: { Accept: "*/*" } },
    "Nie udało się pobrać załącznika.",
    "zalacznik",
  );
}

export async function deleteHqDeviceOperationAttachment(
  deviceId: string,
  attachmentId: string,
) {
  const response = await apiFetch(
    `${apiBaseURL}/v1/hq/devices/${encodeURIComponent(deviceId)}/operation-attachments/${encodeURIComponent(attachmentId)}`,
    {
      method: "DELETE",
      credentials: "include",
      headers: { Accept: "application/json" },
    },
  );
  if (!response.ok)
    await throwApiError(response, "Nie udało się usunąć załącznika.");
  return hqDeviceOperationsResponseSchema.parse(await response.json());
}
